import { type CommonAction, response } from "@iflyrpa/share";
import { z } from "zod";
import { ActionCommonParamsSchema, AxiosProxyParams } from "../../types";
import { Http } from "../../utils/http";
import { Xhshow } from "../../utils/xhsXsEncrypt";

// 小红书参数加密器
const xsEncrypt = new Xhshow();

export const XhsWebSearchParamsSchema = ActionCommonParamsSchema.extend({
    keyword: z.string(),
    page: z.number(),
    page_size: z.number(),
    sort_type: z.enum(["general", "time_descending", "popular_descending"]), // 综合、时间降序、热门降序
    time_filter: z.enum(["不限", "一周内"]),
});

export type XhsWebSearchParams = z.infer<typeof XhsWebSearchParamsSchema>;

export interface SearchResponse {
    code: number;
    success: boolean;
    msg: string;
    data: {
        has_more: boolean;
        item: NoteDetail[];
    };
}

export interface NoteDetail {
    xsec_token: string;
    id: string;
    model_type: string;
    note_card: {
        type: string;
        display_title: string;
        user: {
            nick_name: string;
            avatar: string;
            user_id: string;
            nickname: string;
            xsec_token: string;
        };
        interact_info: {
            shared_count: string;
            liked: boolean;
            liked_count: string;
            collected: boolean;
            collected_count: string;
            comment_count: string;
        };
        cover: {
            height: number;
            width: number;
            url_default: string;
            url_pre: string;
        };
        image_list: Array<{
            height: number;
            width: number;
            info_list: Array<{
                image_scene: string;
                url: string;
            }>;
        }>;
        corner_tag_info: Array<{
            type: string;
            text: string;
        }>;
    };
}

export type PublishAction = CommonAction<XhsWebSearchParams, Record<string, unknown>>;

const createSearchId = () => {
    let r: bigint = BigInt(Date.now());
    const o: bigint = BigInt(Math.ceil(0x7ffffffe * Math.random()));
    r <<= BigInt(64);
    r += o;
    return r.toString(36);
};

export const xiaohongshuWebSearch: PublishAction = async (_task, params) => {
    const headers = {
        cookie: params.cookies.map((it) => `${it.name}=${it.value}`).join(";"),
        referer: "https://www.xiaohongshu.com/",
        origin: "https://www.xiaohongshu.com/",
    };

    const args: AxiosProxyParams = [
        { headers },
        _task.logger,
        params.proxyLoc,
        params.localIP,
        // params.huiwenToken,
        params.accountId,
    ];
    const http = new Http(...args);

    const a1Cookie = params.cookies.find((it) => it.name === "a1")?.value;
    if (!a1Cookie) return { code: 414, message: "账号数据异常，请重新绑定账号后重试。", data: {} };
    if (!params.keyword || !params.page || !params.page_size || !params.sort_type || !params.time_filter) {
        if (
            params.sort_type !== "general" &&
            params.sort_type !== "time_descending" &&
            params.sort_type !== "popular_descending"
        ) {
            return { code: 414, message: "排序类型参数错误，请检查后重试！", data: {} };
        }
        if (params.time_filter !== "不限" && params.time_filter !== "一周内") {
            return { code: 414, message: "时间过滤参数错误，请检查后重试！", data: {} };
        }
        return { code: 414, message: "参数缺失，请检查后重试！", data: {} };
    }

    const filterPath = "/api/sns/web/v1/search/notes";
    const filterParams = {
        ext_flags: [],
        geo: "",
        image_formats: ["jpg", "webp", "avif"],
        filters: [
            { tags: [params.sort_type], type: "sort_type" },
            { tags: ["不限"], type: "filter_note_type" },
            { tags: [params.time_filter], type: "filter_note_time" },
            { tags: ["不限"], type: "filter_note_range" },
            { tags: ["不限"], type: "filter_pos_distance" },
        ],
        keyword: params.keyword,
        note_type: 0,
        page: params.page,
        page_size: params.page_size,
        search_id: createSearchId() + (params.sort_type || params.time_filter ? `@${createSearchId()}` : ""),
        sort: "general",
    };

    const recordCookie = params.cookies.reduce<Record<string, string>>((acc, cookie) => {
        if (cookie.name && cookie.value) {
            acc[cookie.name] = cookie.value;
        }
        return acc;
    }, {});
    const searchXsHeader = xsEncrypt.signHeadersPost(filterPath, recordCookie, "xhs-pc-web", filterParams);

    const res = await http.api<SearchResponse>(
        {
            method: "post",
            url: "https://edith.xiaohongshu.com/api/sns/web/v1/search/notes",
            data: filterParams,
            headers: searchXsHeader,
        },
        { retries: 3, retryDelay: 20, timeout: 3000 },
    );

    const isSuccess = res.code === 0;
    const message = `Web笔记搜索${isSuccess ? "成功" : `失败，原因：${res.msg}`}${_task.debug ? ` ${http.proxyInfo}` : ""}`;
    const data = isSuccess ? res.data : {};

    return response(isSuccess ? 0 : 414, message, data);
};
